import { Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "./SiteLayout";
import { SiteBreadcrumbs } from "./SiteBreadcrumbs";
import { useT } from "@/lib/i18n/dict";

export type HubFeature = {
  title: string;
  desc: string;
  href: string;
  icon: LucideIcon;
  bullets?: string[];
};

export type PlatformHubProps = {
  eyebrow: string;
  title: string;
  italic: string;
  description: string;
  heroImage: string;
  heroImageAlt?: string;
  features: HubFeature[];
  outro: { title: string; italic: string; description: string };
};

export function PlatformHub(p: PlatformHubProps) {
  const t = useT();
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName={p.eyebrow} />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              {p.title} <em className="italic">{p.italic}</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">{p.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
                {t("cta.getStartedFree")} <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/pricing" className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70">
                {t("cta.seePricing")}
              </Link>
            </div>
          </div>
          <div className="relative order-1 md:order-2">
            <div className="absolute -inset-6 rounded-[2rem] bg-brand-lime/10 blur-3xl" aria-hidden />
            <img src={p.heroImage} alt={p.heroImageAlt ?? p.title} width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("platform.whatsInside")}</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              {t("platform.everythingTitle")} <em className="italic">{t("platform.everythingItalic")}</em>
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {p.features.map((f) => {
              const Icon = f.icon;
              return (
                <Link
                  key={f.href}
                  to={f.href}
                  className="group flex flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                  {f.bullets && f.bullets.length > 0 && (
                    <ul className="mt-4 grid gap-2">
                      {f.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-[13px]">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                    {t("cta.learnMore")} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUTRO */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              {p.outro.title} <em className="italic text-brand-lime">{p.outro.italic}</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">{p.outro.description}</p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                {t("cta.getStartedFree")}
              </CtaLink>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                {t("cta.talkToSales")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}